import { useState, useEffect } from "react";
import { CheckCircle, X, Info } from "lucide-react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import AppLayout from "../components/Layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function TabPanel({ children }) {
  return <div className="py-6">{children}</div>;
}

export default function ApprovalManagement() {
  const [tabValue, setTabValue] = useState("masjid");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    fetchApprovalRequests();
  }, []);

  const fetchApprovalRequests = async () => {
    try {
      const requestsRef = collection(db, "approval_requests");
      const q = query(requestsRef, where("status", "==", "pending"));
      const snapshot = await getDocs(q);

      const requestsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setRequests(requestsList);
    } catch (error) {
      console.error("Error fetching approval requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    try {
      // Update user role and status
      await updateDoc(doc(db, "users", request.userId), {
        status: "active",
        approvedAt: new Date().toISOString(),
      });

      // Update approval request
      await updateDoc(doc(db, "approval_requests", request.id), {
        status: "approved",
        approvedAt: new Date().toISOString(),
      });

      // If masjid admin, add to admins collection
      if (request.type === "masjid_admin") {
        await setDoc(doc(db, "admins", request.userEmail), {
          email: request.userEmail,
          role: "masjid_admin",
          masjidId: request.masjidId,
          createdAt: new Date().toISOString(),
        });

        // Update masjid with admin email
        await updateDoc(doc(db, "masjids", request.masjidId), {
          adminEmails: [request.userEmail],
          verified: true,
        });
      }

      // If restaurant admin, create restaurant
      if (request.type === "restaurant_admin") {
        const restaurantId = `restaurant_${request.userId}`;
        await setDoc(doc(db, "restaurants", restaurantId), {
          ...request.restaurantData,
          adminUid: request.userId,
          verified: false,
          createdAt: new Date().toISOString(),
        });

        await updateDoc(doc(db, "users", request.userId), {
          restaurantId,
        });

        await setDoc(doc(db, "admins", request.userEmail), {
          email: request.userEmail,
          role: "restaurant_admin",
          restaurantId,
          createdAt: new Date().toISOString(),
        });
      }

      // TODO: Send approval email

      alert("Request approved successfully!");
      fetchApprovalRequests();
    } catch (error) {
      console.error("Error approving request:", error);
      alert("Failed to approve request");
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason) return;

    try {
      await updateDoc(doc(db, "users", selectedRequest.userId), {
        status: "rejected",
        rejectionReason,
        rejectedAt: new Date().toISOString(),
      });

      await updateDoc(doc(db, "approval_requests", selectedRequest.id), {
        status: "rejected",
        rejectionReason,
        rejectedAt: new Date().toISOString(),
      });

      // TODO: Send rejection email

      alert("Request rejected");
      setDialogOpen(false);
      setSelectedRequest(null);
      setRejectionReason("");
      fetchApprovalRequests();
    } catch (error) {
      console.error("Error rejecting request:", error);
      alert("Failed to reject request");
    }
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setDialogOpen(true);
  };

  const masjidRequests = requests.filter((r) => r.type === "masjid_admin");
  const restaurantRequests = requests.filter(
    (r) => r.type === "restaurant_admin"
  );

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold tracking-tight">
            Approval Management
          </h1>
          <p className="mt-2 text-muted-foreground">
            Review and approve admin access requests
          </p>
        </div>

        {requests.length === 0 && !loading && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              No pending approval requests at this time
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <Tabs value={tabValue} onValueChange={setTabValue}>
            <TabsList className="w-full justify-start">
              <TabsTrigger value="masjid">
                Masjid Admins ({masjidRequests.length})
              </TabsTrigger>
              <TabsTrigger value="restaurant">
                Restaurants ({restaurantRequests.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="masjid" className="mt-6">
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Masjid</TableHead>
                      <TableHead className="font-semibold">Requested</TableHead>
                      <TableHead className="text-right font-semibold">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {masjidRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className="font-medium">
                          {request.userName}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {request.userEmail}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {request.masjidName}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(request.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApprove(request)}
                              title="Approve"
                              className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-900/50"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRejectDialog(request)}
                              title="Reject"
                              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/50"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="View Details"
                            >
                              <Info className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {masjidRequests.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="h-32 text-center">
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <CheckCircle className="h-8 w-8 opacity-30" />
                            <p className="text-sm">
                              No pending masjid admin requests
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="restaurant" className="mt-6">
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">
                        Restaurant
                      </TableHead>
                      <TableHead className="font-semibold">Location</TableHead>
                      <TableHead className="font-semibold">Requested</TableHead>
                      <TableHead className="text-right font-semibold">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {restaurantRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className="font-medium">
                          {request.userName}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {request.userEmail}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {request.restaurantData?.restaurantName}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {request.restaurantData?.city},{" "}
                          {request.restaurantData?.state}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(request.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApprove(request)}
                              title="Approve"
                              className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-900/50"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRejectDialog(request)}
                              title="Reject"
                              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/50"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {restaurantRequests.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="h-32 text-center">
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <CheckCircle className="h-8 w-8 opacity-30" />
                            <p className="text-sm">
                              No pending restaurant requests
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Reject Request</DialogTitle>
              <DialogDescription>
                Provide a clear reason for rejecting this request. The applicant
                will receive this explanation.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason for rejection *</Label>
                <Textarea
                  id="reason"
                  rows={5}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a detailed explanation..."
                  className="resize-none text-base"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Be specific and professional in your feedback
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                size="lg"
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={!rejectionReason}
                size="lg"
                className="rounded-xl shadow-lg"
              >
                <X className="mr-2 h-5 w-5" />
                Reject Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
